/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import Fuse from 'fuse.js';
import { atom, useAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { RESET, atomWithStorage } from 'jotai/utils';
import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { AtomButton } from '../Button';
import { IsolateField } from '../Form';
import { Tab, Tabs } from '../Tab';
import { TextInput } from '../TextInput';
import { Tooltip } from '../Tooltip';
import { type IconDefinition, pxToRem, renderIcon } from '../util';

export interface GenericPickerCategory {
    key: string;
    label: string;
    icon: IconDefinition;
}

export interface GenericPickerRenderInfo {
    label: string;
    icon: IconDefinition;
}

export interface GenericPickerProps<Item> {
    /**
     * ID of the list. Used for persistence.
     */
    listId: string;

    /**
     * Title to render for the list.
     */
    title: string;

    /**
     * Items to render.
     */
    items: Item[];

    /**
     * Callback to render an item.
     */
    renderItem: (item: Item) => GenericPickerRenderInfo;

    /**
     * Get the category for an item.
     */
    getCategory?: (item: Item) => string;

    /**
     * Get the key for an item.
     */
    getKey: (item: Item) => string;

    /**
     * Categories to display.
     */
    categories?: GenericPickerCategory[];

    /**
     * Keys to search text into.
     */
    textKeys: Fuse.FuseOptionKey[];

    /**
     * Callback when an item is selected.
     */
    onPick: (item: Item) => void;

    /**
     * Optional query to filter the icons.
     * If a query is passed, a callback onChangeQuery should be passed to update the query.
     */
    query?: string;

    /**
     * Callback when the query changes.
     */
    onChangeQuery?: (query: string) => void;

    /**
     * Header to render above the search input.
     */
    header?: React.ReactNode;

    /**
     * Size of the icon/emoji in pixels. Defaults to 24.
     * @default 24
     */
    iconSize?: number;

    /**
     * Whether to ignore the keys in the keyboard navigation. This can be useful when the picker is used in a context where the arrow keys are used for other purposes (e.g. in a select input).
     */
    ignoreKeys?: boolean;

    /**
     * Placeholder text for the search input.
     */
    placeholder?: string;
}

interface GenericPickerComputation<Item> {
    rows: number;
    groups: Array<{
        key: string;
        /** Index of the row representing this category */
        row: number;
        /** Items in this category  */
        items: Item[];
    }>;
}

const ICONS_PER_ROW = 9;
const NUMBER_OF_ROWS = 2;
const MAX_RECENTLY_USED = ICONS_PER_ROW * NUMBER_OF_ROWS;
const ITEM_SIZE = 32; /* Size of emoji/icon button in the grid */
const ICON_SIZE = 24; /* Default size of the inner emoji/icon in each button. Can be overridden by the iconSize prop */
const GROUP_ROW_HEIGHT = ICON_SIZE;

const recentlyUsedAtom = atomWithStorage<Record<string, string[]>>('recentlyUsed', {}, undefined, {
    getOnInit: true,
});

const recentlyUsedSelector = atomFamily((key: string) =>
    atom(
        (get) => get(recentlyUsedAtom)[key] ?? [],
        (get, set, update: string[] | ((previous: string[]) => string[]) | typeof RESET) => {
            const previous = get(recentlyUsedAtom)[key] ?? [];
            const next =
                update === RESET ? [] : typeof update === 'function' ? update(previous) : update;
            set(recentlyUsedAtom, (oldValue) => ({
                ...oldValue,
                [key]: next,
            }));
        }
    )
);

/**
 * Generic picker component, used for emojis or icons.
 * It supports filtering, categories, and keyboard navigation.
 */
export function GenericPicker<Item>(props: GenericPickerProps<Item>) {
    const {
        listId,
        title,
        items: allItems,
        textKeys,
        categories,
        getKey,
        header,
        iconSize = ICON_SIZE,
        ignoreKeys = false,
        placeholder = 'Filter…',
    } = props;

    const parentRef = React.useRef<HTMLDivElement>(null);
    const searchInputRef = React.useRef<HTMLInputElement>(null);
    const [queryState, setQueryState] = React.useState('');
    const [recentlyUsed, setRecentlyUsed] = useAtom(recentlyUsedSelector(listId));
    const onPick = useEventCallback((item: Item) => {
        setRecentlyUsed((prev) => pushRecentlyUsed(prev, getKey(item)));
        props.onPick(item);
    });

    const query = props.query ?? queryState;
    const setQuery = props.onChangeQuery ?? setQueryState;

    React.useLayoutEffect(() => {
        if (focusSearchInput(searchInputRef.current)) {
            return;
        }

        // Popover content can mount before its portal flips from `display: none` to visible, which
        // makes the first focus call a no-op. Retry once after layout has settled.
        const animationFrameIndex = requestAnimationFrame(() => {
            focusSearchInput(searchInputRef.current);
        });

        return () => {
            cancelAnimationFrame(animationFrameIndex);
        };
    }, []);

    // Computes the items to display based on the query.
    const computed = React.useMemo(() => {
        if (!query) {
            const cached = getItemsByCategories(allItems, props.getCategory ?? (() => ''));
            return recentlyUsed.length > 0
                ? prependGroups(cached, [
                      {
                          key: 'recently-used',
                          items: allItems
                              .filter((item) => recentlyUsed.includes(getKey(item)))
                              .sort((a, b) => {
                                  return (
                                      recentlyUsed.indexOf(getKey(a)) -
                                      recentlyUsed.indexOf(getKey(b))
                                  );
                              }),
                      },
                  ])
                : cached;
        }

        const fuse = getItemsFuse(allItems, textKeys);
        const items = fuse.search(query).map((result) => result.item);
        if (!items.length) {
            return null;
        }

        return {
            rows: 1 + Math.ceil(items.length / ICONS_PER_ROW),
            groups: [{ key: '', row: 0, items }],
        };
    }, [allItems, textKeys, query, getKey, recentlyUsed, props.getCategory]);

    const virtualizer = useVirtualizer({
        count: computed ? computed.rows : 1,
        overscan: 7,
        getScrollElement: () => parentRef.current,
        estimateSize: (index) => {
            if (computed && isGroupRow(computed, index)) {
                return computed.groups.length === 1 ? 0 : GROUP_ROW_HEIGHT;
            }

            return ITEM_SIZE;
        },
    });

    // Compute the active group based on the current visible range in the virtual list.
    const activeGroup =
        !query && categories && computed && virtualizer.range
            ? getActiveGroup(virtualizer.range, computed)
            : null;

    // Handle keyboard navigation
    const { cursor, onHoverItem } = useGenericPickerKeyboardNavigation(
        computed,
        onPick,
        ignoreKeys
    );

    // Scroll to ensure the cursor is visible
    React.useEffect(() => {
        virtualizer.scrollToIndex(cursor?.row ?? 0);
    }, [cursor?.row, virtualizer]);

    const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(event.target.value);
    };

    const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // Prevent the default behavior of the arrow keys as it's being handled by useGenericPickerKeyboardNavigation
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
            event.preventDefault();
        }
    };

    const padding = 2 * 12;
    const gap = 2 * (ICONS_PER_ROW - 1);
    const width = padding + gap + ICONS_PER_ROW * ITEM_SIZE;

    return (
        <div className="flex h-80 flex-col" style={{ width: pxToRem(width) }}>
            <div className="p-3">
                {header}
                <IsolateField>
                    <TextInput
                        ref={searchInputRef}
                        leadingIcon={Icon.Search}
                        value={query}
                        onChange={onSearchChange}
                        onKeyDown={onSearchKeyDown}
                        placeholder={placeholder}
                    />
                </IsolateField>
            </div>
            {!query && categories ? (
                <Tabs gap={false} className="pr-4.5 pl-3">
                    {/* pr-4.5 to ensure category tabs still align with emojis (accounts the emoji list's scrollbar) */}
                    {recentlyUsed.length > 0 ? (
                        <Tab
                            leadingIcon={Icon.Clock}
                            iconOnly
                            label="Recently used"
                            onPress={() => {
                                virtualizer.scrollToIndex(0, {
                                    align: 'start',
                                    behavior: 'smooth',
                                });
                            }}
                            active={activeGroup === 'recently-used'}
                        />
                    ) : null}
                    {categories.map((category) => (
                        <Tab
                            key={category.key}
                            leadingIcon={category.icon}
                            iconOnly
                            label={category.label}
                            onPress={() => {
                                const index =
                                    computed?.groups.find((group) => group.key === category.key)
                                        ?.row ?? 0;
                                virtualizer.scrollToIndex(index, {
                                    align: 'start',
                                    behavior: 'smooth',
                                });
                            }}
                            active={activeGroup === category.key}
                        />
                    ))}
                </Tabs>
            ) : null}
            {computed ? (
                <div
                    ref={parentRef}
                    className="scroll-grad scrollbar flex-1 overflow-x-hidden overflow-y-auto scroll-gradient-mask-y [--mask-height:0.5rem] [scrollbar-gutter:stable]"
                >
                    <div
                        style={{
                            height: `${virtualizer.getTotalSize()}px`,
                        }}
                        className="relative w-full"
                    >
                        {virtualizer.getVirtualItems().map((virtualItem) => {
                            const row = getRow(computed, virtualItem.index);

                            return (
                                <div
                                    key={virtualItem.index}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: `${virtualItem.size}px`,
                                        transform: `translateY(${virtualItem.start}px)`,
                                    }}
                                    className="flex gap-0.5 px-3"
                                >
                                    {row.items ? (
                                        row.items.map((item, column) => {
                                            const info = props.renderItem(item);
                                            const itemCursor: GenericPickerCursor = {
                                                row: virtualItem.index,
                                                column,
                                            };

                                            const isActive =
                                                cursor?.row === itemCursor.row &&
                                                cursor?.column === itemCursor.column;

                                            return (
                                                <Tooltip
                                                    label={info.label}
                                                    key={getKey(item)}
                                                    disabled={virtualizer.isScrolling}
                                                >
                                                    <AtomButton
                                                        className={clsx(
                                                            'flex items-center justify-center rounded-md hover:bg-neutral-ui-hover focus:focus-ring',
                                                            isActive && 'bg-neutral-ui-active'
                                                        )}
                                                        style={{
                                                            width: ITEM_SIZE,
                                                            height: ITEM_SIZE,
                                                        }}
                                                        aria-label={info.label}
                                                        onPress={() => {
                                                            onPick(item);
                                                        }}
                                                        onMouseEnter={() => onHoverItem(itemCursor)}
                                                    >
                                                        {renderIcon(info.icon, {
                                                            size: iconSize,
                                                        })}
                                                    </AtomButton>
                                                </Tooltip>
                                            );
                                        })
                                    ) : computed.groups.length > 1 ? (
                                        <PickerGroup>
                                            {getGroupLabel(row.group, categories ?? [], title)}
                                        </PickerGroup>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <div className="flex items-center justify-center p-4">
                    <span className="text-secondary">No results</span>
                </div>
            )}
        </div>
    );
}

export function PickerGroup(
    props: Pick<React.ComponentPropsWithoutRef<'div'>, 'className' | 'children'>
) {
    return (
        <div
            {...props}
            className={clsx('flex items-end pb-0.5 text-ui-small text-secondary', props.className)}
        />
    );
}

// Cache the fuses for each items set to optimize performance.
const fuses = new WeakMap<Array<any>, Fuse<any>>();
function getItemsFuse<Item>(allItems: Item[], textKeys: Fuse.FuseOptionKey[]) {
    let fuse = fuses.get(allItems) as Fuse<Item> | undefined;
    if (!fuse) {
        fuse = new Fuse(allItems, {
            keys: textKeys,
            threshold: 0.3,
        });
        fuses.set(allItems, fuse);
    }

    return fuse;
}

/**
 * Focus the search input only when the browser accepts the focus, so hidden popover mounts can
 * retry after the overlay becomes visible.
 */
function focusSearchInput(input: HTMLInputElement | null) {
    input?.focus({ preventScroll: true });
    return document.activeElement === input;
}

// Cache the grouping of items by category to optimize performance.
const itemCategories = new WeakMap<Array<any>, GenericPickerComputation<any>>();
function getItemsByCategories<Item>(
    items: Item[],
    getGroup: (item: any) => string
): GenericPickerComputation<Item> {
    let computation = itemCategories.get(items) as GenericPickerComputation<Item> | undefined;
    if (!computation) {
        const groups = new Map<string, Item[]>();
        for (const item of items) {
            const category = getGroup(item);
            if (!groups.has(category)) {
                groups.set(category, []);
            }
            groups.get(category)?.push(item);
        }

        computation = {
            rows: 0,
            groups: [],
        };

        for (const [key, items] of groups.entries()) {
            computation.groups.push({
                key,
                row: computation.rows,
                items,
            });
            computation.rows += Math.ceil(items.length / ICONS_PER_ROW) + 1;
        }

        itemCategories.set(items, computation);
    }

    return computation;
}

/**
 * Prepend some groups to the computation.
 */
function prependGroups<Item>(
    computation: GenericPickerComputation<Item>,
    groups: Array<{ key: string; items: Item[] }>
): GenericPickerComputation<Item> {
    const result: GenericPickerComputation<Item> = {
        rows: 0,
        groups: [],
    };

    for (const group of groups) {
        result.groups.push({
            key: group.key,
            row: result.rows,
            items: group.items,
        });
        result.rows += Math.ceil(group.items.length / ICONS_PER_ROW) + 1;
    }

    for (const category of computation.groups) {
        result.groups.push({
            key: category.key,
            row: result.rows,
            items: category.items,
        });
        result.rows += Math.ceil(category.items.length / ICONS_PER_ROW) + 1;
    }

    return result;
}

/**
 * Return true if a row is a group.
 */
function isGroupRow(computation: GenericPickerComputation<any>, row: number) {
    return computation.groups.some((group) => group.row === row);
}

/**
 * Compute what to render for a row index.
 */
function getRow<Item>(
    computation: GenericPickerComputation<Item>,
    row: number
):
    | {
          group: string;
          items?: undefined;
      }
    | {
          group?: undefined;
          items: Item[];
      } {
    for (const category of computation.groups) {
        if (category.row === row) {
            return { group: category.key };
        }

        const rowsInCategory = Math.ceil(category.items.length / ICONS_PER_ROW);

        if (row > category.row && row <= category.row + rowsInCategory) {
            const relativeIndex = row - category.row - 1;
            return {
                items: category.items.slice(
                    relativeIndex * ICONS_PER_ROW,
                    (relativeIndex + 1) * ICONS_PER_ROW
                ),
            };
        }
    }

    throw new Error('Invalid row index');
}

/**
 * Push a new item to the recently used list.
 */
function pushRecentlyUsed(recentlyUsed: string[], key: string) {
    return [key, ...recentlyUsed.filter((item) => item !== key)].slice(0, MAX_RECENTLY_USED);
}

/**
 * Get the label for a group.
 */
export function getGroupLabel(
    group: string,
    categories: GenericPickerCategory[],
    defaultLabel: string
) {
    if (group === 'recently-used') {
        return 'Recently used';
    }

    const category = categories.find((c) => c.key === group);
    if (category) {
        return category.label;
    }

    return defaultLabel;
}

/**
 * Get the key of the current group visible in a range.
 */
function getActiveGroup(
    range: { startIndex: number; endIndex: number },
    computed: GenericPickerComputation<any>
): string | null {
    let activeGroup: string | null = null;

    for (const group of computed.groups) {
        const groupRangeStart = group.row;
        const groupRangeEnd = group.row + Math.ceil(group.items.length / ICONS_PER_ROW) + 1;

        if (range.startIndex >= groupRangeStart && range.startIndex < groupRangeEnd) {
            activeGroup = group.key;
            break;
        }

        if (range.endIndex >= groupRangeStart && range.endIndex < groupRangeEnd) {
            activeGroup = group.key;
            break;
        }

        if (range.startIndex <= groupRangeStart && range.endIndex >= groupRangeEnd) {
            activeGroup = group.key;
            break;
        }
    }

    return activeGroup;
}

interface GenericPickerCursor {
    row: number;
    column: number;
}

/**
 * Hook to handle the keyboard navigation in the grid.
 */
function useGenericPickerKeyboardNavigation<Item>(
    computed: GenericPickerComputation<Item> | null,
    onPick: (item: Item) => void,
    disabled: boolean = false
): {
    cursor: GenericPickerCursor | null;
    onHoverItem: (hover: GenericPickerCursor) => void;
} {
    const [cursor, setCursor] = React.useState<GenericPickerCursor | null>(null);

    const onSubmit = useEventCallback(() => {
        if (cursor && computed) {
            const row = getRow(computed, cursor.row);
            if (row.items) {
                onPick(row.items[cursor.column]);
            }
        }
    });

    // Reset the cursor when the items change.
    React.useEffect(() => {
        setCursor(computed ? findInitialCursor(computed) : null);
    }, [computed]);

    // Listen to global keyboard events and move the cursor accordingly.
    React.useEffect(() => {
        if (disabled || !computed) {
            return;
        }

        const onKeydown = (event: KeyboardEvent) => {
            if (disabled) {
                return;
            }

            if (event.key === 'ArrowUp') {
                setCursor((cursor) => moveCursor(computed, cursor, { row: -1, column: 0 }));
            } else if (event.key === 'ArrowDown') {
                setCursor((cursor) => moveCursor(computed, cursor, { row: 1, column: 0 }));
            } else if (event.key === 'ArrowLeft') {
                setCursor((cursor) => moveCursor(computed, cursor, { row: 0, column: -1 }));
            } else if (event.key === 'ArrowRight') {
                setCursor((cursor) => moveCursor(computed, cursor, { row: 0, column: 1 }));
            } else if (event.key === 'Enter') {
                onSubmit();
            }
        };

        window.addEventListener('keydown', onKeydown);
        return () => {
            window.removeEventListener('keydown', onKeydown);
        };
    }, [computed, onPick, onSubmit, disabled]);

    return {
        cursor,
        onHoverItem: (hover: GenericPickerCursor) => {
            setCursor(hover);
        },
    };
}

/**
 * Move the cursor in the grid, skipping the group rows.
 */
function moveCursor<Item>(
    computed: GenericPickerComputation<Item>,
    cursor: GenericPickerCursor | null,
    move: {
        row: 0 | 1 | -1;
        column: 0 | 1 | -1 | 'start' | 'end';
    }
): GenericPickerCursor | null {
    let result: GenericPickerCursor;
    if (!cursor) {
        const initial = findInitialCursor(computed);
        if (!initial) {
            return null;
        }
        result = initial;
    } else {
        result = { ...cursor };
    }

    // First move the row
    if (move.row) {
        result.row = result.row + move.row;
        if (result.row < 0 || result.row >= computed.rows) {
            const next = findInitialCursor(computed, result.column);
            if (!next) {
                return null;
            }
            result = next;
        }

        const row = getRow(computed, result.row);
        if (!row.items) {
            // Move to the next/previous row
            const next = moveCursor(computed, result, { row: move.row, column: 0 });
            if (!next) {
                return null;
            }
            result = next;
        }
    }

    // Then move the column
    if (move.column) {
        const row = getRow(computed, result.row);
        if (!row.items) {
            // TODO
            return result;
        }

        if (move.column === 'start') {
            result.column = 0;
        } else if (move.column === 'end') {
            result.column = row.items.length - 1;
        } else {
            const maxColumns = row.items.length;
            result.column = result.column + move.column;
            if (result.column >= maxColumns) {
                const next = moveCursor(computed, result, { row: 1, column: 'start' });
                if (!next) {
                    return null;
                }
                result = next;
            } else if (result.column < 0) {
                const next = moveCursor(computed, result, { row: -1, column: 'end' });
                if (!next) {
                    return null;
                }
                result = next;
            }
        }
    }

    return normalizeCursor(computed, result);
}

/**
 * Normalize the cursor position, to ensure it is on an item.
 */
function normalizeCursor<Item>(
    computed: GenericPickerComputation<Item>,
    cursor: GenericPickerCursor
): GenericPickerCursor | null {
    const row = getRow(computed, cursor.row);
    if (!row.items || !row.items.length) {
        return findInitialCursor(computed);
    }
    if (cursor.column < 0) {
        return { row: cursor.row, column: 0 };
    }
    if (cursor.column >= row.items.length) {
        return { row: cursor.row, column: row.items.length - 1 };
    }

    return cursor;
}

/**
 * Find the initial cursor for a grid.
 */
function findInitialCursor<Item>(
    computed: GenericPickerComputation<Item>,
    column = 0
): GenericPickerCursor | null {
    for (let row = 0; row < computed.rows; row++) {
        const rowInfo = getRow(computed, row);
        if (rowInfo.items && rowInfo.items.length > 0) {
            return {
                row,
                column: Math.max(0, Math.min(column, rowInfo.items.length - 1)),
            };
        }
    }

    return null;
}
